// Account: what you have, what you have spent, and the keys you can spend
// it with. The balance leads because it is the number that decides what
// the next run can be.
import { useEffect, useState } from "react";
import { createApiKey, deleteApiKey, getAccountUsage, listApiKeys, type ApiKey, type UsageRecord } from "../api/client";
import { formatMinor, formatWhen, ledgerAmount } from "../lib/format";
import { useAuth } from "../auth/AuthContext";

export default function Account() {
  const { user } = useAuth();
  const [usage, setUsage] = useState<UsageRecord[]>([]);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [newKeyLabel, setNewKeyLabel] = useState("");
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function loadUsage() {
    getAccountUsage().then(setUsage).catch(() => undefined);
  }
  function loadKeys() {
    listApiKeys().then(setKeys).catch(() => undefined);
  }

  useEffect(() => {
    loadUsage();
    loadKeys();
  }, []);

  async function onCreateKey() {
    setError(null);
    try {
      const created = await createApiKey(newKeyLabel || undefined);
      setNewSecret(created.secret);
      setNewKeyLabel("");
      loadKeys();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to create key");
    }
  }

  async function onRevoke(id: string) {
    await deleteApiKey(id);
    loadKeys();
  }

  if (!user) return null;

  return (
    <div className="u-page">
      <div className="u-head">
        <div>
          <h1 className="u-h1">Account</h1>
          <p className="u-lede">{user.email}</p>
        </div>
        <span className="u-balance">{formatMinor(user.balance, user.currency)}</span>
      </div>

      <section className="u-card" style={{ marginTop: 26 }}>
        <h2 className="u-h2">API keys</h2>
        <p className="u-note">
          A key lets a script or another machine start runs as you. It is shown once, at creation,
          and can be revoked at any time.
        </p>
        {newSecret && (
          <p className="u-key">
            <span className="u-tag">New key</span>
            <code>{newSecret}</code>
            <button className="u-btn u-btn--quiet" onClick={() => setNewSecret(null)}>
              Dismiss
            </button>
          </p>
        )}
        <div className="u-field u-field--row">
          <input
            className="u-input"
            placeholder="label (optional)"
            value={newKeyLabel}
            onChange={(e) => setNewKeyLabel(e.target.value)}
          />
          <button className="u-btn" onClick={() => void onCreateKey()}>
            Create key
          </button>
        </div>
        {error && <p className="u-error">{error}</p>}
        <table className="u-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Created</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {keys.length === 0 ? (
              <tr>
                <td colSpan={4} className="u-empty">
                  No keys yet.
                </td>
              </tr>
            ) : (
              keys.map((k) => (
                <tr key={k.id}>
                  <td>{k.label || "—"}</td>
                  <td>{formatWhen(k.created_at)}</td>
                  <td>
                    <span className={k.revoked_at ? "u-state u-state--cancelled" : "u-state u-state--done"}>
                      {k.revoked_at ? "revoked" : "active"}
                    </span>
                  </td>
                  <td>
                    {!k.revoked_at && (
                      <button className="u-btn u-btn--quiet" onClick={() => void onRevoke(k.id)}>
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      <section className="u-card" style={{ marginTop: 26 }}>
        <h2 className="u-h2">Usage</h2>
        <p className="u-note">
          Every charge and refund on this account, newest last, alongside what each run cost the
          platform in GPU time — a cost is recorded here but never charged to you. Charges are
          taken when a run is accepted; a result that fails is refunded.
        </p>
        <table className="u-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Kind</th>
              <th>Amount</th>
              <th>Run</th>
            </tr>
          </thead>
          <tbody>
            {usage.length === 0 ? (
              <tr>
                <td colSpan={4} className="u-empty">
                  Nothing spent yet.
                </td>
              </tr>
            ) : (
              usage.map((u) => (
                <tr key={u.id}>
                  <td>{formatWhen(u.created_at)}</td>
                  <td>{u.kind.toLowerCase()}</td>
                  <td
                    className="u-num"
                    title={u.kind.toUpperCase() === "COST" ? "what this run cost in GPU time; not charged to this account" : undefined}
                  >
                    {ledgerAmount(u.kind, u.amount_minor, user.currency)}
                  </td>
                  <td>{u.task_id ? u.task_id.slice(0, 8) : "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
